import { type FC, useState } from "react"
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity,
  View,
} from "react-native"
import { useRedfinAutocomplete } from "@/queries/markettrends"
import { debounce } from "lodash"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { Body, H4 } from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"
import PlaceIcon from "@/assets/icons/place-white.svg"
import { useViewedCallback } from "@/analytics/react"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { useSafeAreaInsets } from "react-native-safe-area-context"

interface MarketTrendsSearchProps {
  onBack: () => void
  onSelectRegion: (region: string | null) => void
}

const MarketTrendsSearch: FC<MarketTrendsSearchProps> = ({
  onBack,
  onSelectRegion,
}) => {
  const [query, setQuery] = useState("")
  const { data: regionResults, isLoading } = useRedfinAutocomplete(query) // TODO: Consider sending lat/long as well for better results

  useViewedCallback(() => {
    setStatusBarStyle("light")
  })

  const debouncedSetQuery = debounce(setQuery, 300)

  const onChangeText = (text: string) => {
    debouncedSetQuery(text)
  }

  const insets = useSafeAreaInsets()

  return (
    <BFScreen dark={true} gradientBackground={true}>
      <KeyboardAvoidingView
        className="flex flex-col flex-1 min-w-[400px]"
        behavior="padding"
      >
        <View
          className="flex flex-col px-6 pb-2"
          style={{ marginTop: insets.top + 20 }} // Adjust based on insets.top for consistency between devices
        >
          <View className="flex-col" style={{ marginTop: 26 }}>
            {/* Margin between text and top container */}
            <H4 className="text-lightest">Market Trends for</H4>
            <BFTextInput
              testID="market-trends-search-text-input"
              placeholder="City, State or ZIP Code"
              autoCorrect={false}
              spellCheck={false}
              autoFocus={true}
              variant={"formDark"}
              onChangeText={onChangeText}
            />
          </View>
        </View>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          className="flex flex-col flex-1 mt-3"
        >
          {regionResults?.length === 0 && (
            <View className="flex items-center justify-center py-10">
              <Body className="text-off-white mt-40 text-center">{`No results found in the Redfin market data.\nPlease try another City or ZIP code.`}</Body>
            </View>
          )}

          {regionResults?.map((region, index) => (
            <TouchableOpacity
              testID={`region-${region?.region || index}`}
              key={region?.region || index}
              onPress={() => {
                if (!region?.region) {
                  return
                }
                onSelectRegion(region.region)
              }}
            >
              <View className="flex flex-row h-[65px] mx-6 border-b border-b-light-grey">
                <View className="flex items-center justify-center mr-2">
                  <PlaceIcon width={18} height={19} />
                </View>
                <View className="flex flex-col flex-1 justify-center">
                  <Body className="text-off-white">
                    {region?.region || "N/A"}
                  </Body>
                </View>
              </View>
            </TouchableOpacity>
          ))}
          {isLoading && <ActivityIndicator className="pt-10" size={"small"} />}
        </ScrollView>
      </KeyboardAvoidingView>
    </BFScreen>
  )
}

export default MarketTrendsSearch
